/**
 * CredentialsScreen (SaaS Phase A / A2 — per-provider).
 *
 * The user picks a provider from a list (loaded from GET /providers) and adds a
 * single key; the screen shows which services (STT / translation / TTS) that key
 * will unlock. Providers that offer only part of the services (e.g. ElevenLabs =
 * TTS only) are handled by the catalog's capabilities. Keys are encrypted server
 * side and never returned.
 */

import { useEffect, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import {
  credentialsApi,
  SERVICE_LABELS,
  type CredentialStatus,
  type ProviderInfo,
  type ProviderService,
} from '@/features/credentials/credentialsApi';

function serviceBadges(services: ProviderService[]): string {
  return services.map((s) => SERVICE_LABELS[s]).join(' · ');
}

export function CredentialsScreen(): ReactElement {
  const [providers, setProviders] = useState<ProviderInfo[]>([]);
  const [items, setItems] = useState<CredentialStatus[]>([]);
  const [providerId, setProviderId] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedProvider = useMemo(
    () => providers.find((p) => p.id === providerId) ?? null,
    [providers, providerId],
  );

  async function refresh(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      const [provs, creds] = await Promise.all([
        credentialsApi.listProviders(),
        credentialsApi.list(),
      ]);
      setProviders(provs);
      setItems(creds);
      // Default the picker to the first provider once loaded.
      if (provs.length > 0) {
        setProviderId((cur) => cur || provs[0].id);
      }
    } catch {
      setError('Não foi possível carregar providers/credenciais.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function handleAdd(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!providerId) return;
    setSaving(true);
    setError(null);
    try {
      await credentialsApi.create({ provider: providerId, api_key: apiKey });
      setApiKey('');
      await refresh();
    } catch {
      setError('Falha ao salvar a credencial. Verifique a chave e tente novamente.');
    } finally {
      setSaving(false);
    }
  }

  async function handleRemove(id: string): Promise<void> {
    setError(null);
    try {
      await credentialsApi.remove(id);
      setItems((prev) => prev.filter((c) => c.id !== id));
    } catch {
      setError('Falha ao remover a credencial.');
    }
  }

  return (
    <main style={styles.page}>
      <header style={styles.header}>
        <Link to="/" style={styles.backLink}>
          ← Voltar ao console
        </Link>
        <h1 style={styles.title}>Credenciais de provider</h1>
        <p style={styles.subtitle}>
          Selecione um provider e adicione uma única chave — ela habilita todos os serviços que o
          provider oferece. As chaves são criptografadas no servidor e nunca exibidas novamente.
        </p>
      </header>

      <form onSubmit={(e) => void handleAdd(e)} style={styles.form}>
        <label style={styles.field}>
          <span style={styles.label}>Provider</span>
          <select
            required
            value={providerId}
            onChange={(e) => setProviderId(e.target.value)}
            style={styles.input}
            disabled={loading || providers.length === 0}
          >
            {providers.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </label>

        {selectedProvider && (
          <p style={styles.capabilities}>
            Esta chave habilitará:{' '}
            <strong>{serviceBadges(selectedProvider.capabilities)}</strong>
          </p>
        )}

        <label style={styles.field}>
          <span style={styles.label}>
            {selectedProvider?.key_label ?? 'API key'}
          </span>
          <input
            type="password"
            required
            autoComplete="off"
            placeholder="cole a chave — enviada uma única vez"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            style={styles.input}
          />
        </label>
        <button type="submit" style={styles.primaryButton} disabled={saving || !providerId}>
          {saving ? 'Salvando…' : 'Adicionar credencial'}
        </button>
      </form>

      {error && <p style={styles.error}>{error}</p>}

      <section style={styles.list}>
        <h2 style={styles.listTitle}>Credenciais salvas</h2>
        {loading ? (
          <p style={styles.muted}>Carregando…</p>
        ) : items.length === 0 ? (
          <p style={styles.muted}>Nenhuma credencial cadastrada.</p>
        ) : (
          <ul style={styles.ul}>
            {items.map((c) => {
              const label = providers.find((p) => p.id === c.provider)?.label ?? c.provider;
              return (
                <li key={c.id} style={styles.item}>
                  <span>
                    <strong>{label}</strong>{' '}
                    <span style={styles.badge}>{serviceBadges(c.services)}</span>
                  </span>
                  <button
                    type="button"
                    style={styles.removeButton}
                    onClick={() => void handleRemove(c.id)}
                  >
                    Remover
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </main>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    maxWidth: 640,
    margin: '0 auto',
    padding: '2rem 1.5rem',
    color: 'var(--rt-text)',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.5rem',
  },
  header: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  backLink: {
    color: 'var(--rt-primary)',
    textDecoration: 'none',
    fontSize: '0.85rem',
    marginBottom: '0.25rem',
  },
  title: { margin: 0, fontSize: '1.4rem' },
  subtitle: { margin: 0, color: 'var(--rt-text-muted)', fontSize: '0.9rem' },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    background: 'var(--rt-surface)',
    border: '1px solid var(--rt-border)',
    borderRadius: 12,
    padding: '1.25rem',
  },
  field: { display: 'flex', flexDirection: 'column', gap: '0.35rem', flex: 1, minWidth: 180 },
  label: { fontSize: '0.8rem', color: 'var(--rt-text-muted)' },
  capabilities: {
    margin: 0,
    fontSize: '0.85rem',
    color: 'var(--rt-text-muted)',
  },
  input: {
    padding: '0.6rem',
    borderRadius: 8,
    border: '1px solid var(--rt-border)',
    background: 'var(--rt-bg)',
    color: 'var(--rt-text)',
    fontSize: '0.9rem',
  },
  primaryButton: {
    padding: '0.65rem 1rem',
    borderRadius: 8,
    border: 'none',
    background: 'var(--rt-primary)',
    color: 'var(--rt-primary-text)',
    cursor: 'pointer',
    fontSize: '0.9rem',
    alignSelf: 'flex-start',
  },
  list: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  listTitle: { margin: 0, fontSize: '1.05rem' },
  ul: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  item: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    border: '1px solid var(--rt-border)',
    borderRadius: 8,
    background: 'var(--rt-surface)',
  },
  badge: {
    fontSize: '0.7rem',
    padding: '0.15rem 0.4rem',
    borderRadius: 999,
    border: '1px solid var(--rt-border)',
    color: 'var(--rt-text-muted)',
  },
  removeButton: {
    border: '1px solid var(--rt-border)',
    background: 'transparent',
    color: 'var(--rt-danger)',
    borderRadius: 8,
    padding: '0.35rem 0.7rem',
    cursor: 'pointer',
    fontSize: '0.8rem',
  },
  muted: { color: 'var(--rt-text-muted)', fontSize: '0.9rem', margin: 0 },
  error: { color: 'var(--rt-danger)', fontSize: '0.85rem', margin: 0 },
};
